import cx from 'classnames'
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { noop } from 'lodash'

import Styles from './AnnualUpgrade.module.scss'

import { Modal, useModals } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { useCurrentPlan } from 'hooks/useCurrentPlan'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { usePaymentPlans } from 'hooks/queries/usePaymentPlans'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { Text } from 'blaze-ui/Text/Text'
import { UserRole, Workspace } from 'types'
import { PaymentPlan } from 'api/clients/PaymentPlanClient'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useMutation } from 'react-query'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { useUserRole } from 'hooks/useUserRole'

const AnnualUpgradeModal = ({
  onCancel,
  workspace,
  currentPlan,
  isAdmin,
  adminEmail,
}: {
  onCancel: () => void
  workspace: Workspace
  currentPlan: PaymentPlan
  isAdmin: boolean
  adminEmail: string
}) => {
  const { openModal } = useModals()

  const { mutate: updateSubscription, isLoading: isUpdating } = useMutation(
    async ({ workspaceId, priceId }: { workspaceId: number; priceId: string }) =>
      WorkspaceClient.updateSubscription(workspaceId, priceId),
    {
      onSuccess: (res) => {
        if (res.data.success) {
          const nextInvoiceDate = dayjs(res.data.plan_details?.next_payment?.due_date).format('MMMM D, YYYY')
          showToastMessage("You've upgraded to annual plan!", 'pink')
          openModal(SuccessModal, { onCancel, nextInvoiceDate }, { replace: true })
        } else {
          showToastMessage('We were unable to upgrade your plan', 'error')
        }
      },
      onError: () => {
        showToastMessage('We were unable to upgrade your plan', 'error')
      },
    },
  )

  const upgradeToAnnualPlan = async () => {
    if (!currentPlan?.price || !workspace) {
      showToastMessage('We were unable to upgrade your plan', 'error')

      return
    }

    updateSubscription({ workspaceId: workspace.id, priceId: currentPlan.price.year.price_id })
  }

  return (
    <Modal.Root onPressOutside={onCancel}>
      <div className={Styles.content}>
        <div className={Styles.imgContainer}>
          <h1 className={Styles.header}>Get 3 free months by upgrading to annual billing</h1>
        </div>
        <div className={Styles.description}>
          <Text variant="primary">
            Your workspace "{workspace.name}" is currently at the ${currentPlan.price.month.unit_amount} Pro Plan
            monthly rate. If you switch to annual billing, we'll bill you ${currentPlan.price.year.unit_amount} per
            year, <span className={Styles.bold}>resulting in three free months!</span>
          </Text>
        </div>
      </div>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button size="lg" variant="tertiary" onPress={onCancel}>
            Cancel
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          {isAdmin ? (
            <div className={Styles.footerRightSlot}>
              <div className={Styles.prices}>
                <Text variant="metadata" color="var(--dark-60)">
                  ${currentPlan.price.month.unit_amount * 12}/yr
                </Text>
                <Text variant="primary" color="var(--dark-80)">
                  ${currentPlan.price.year.unit_amount}/yr
                </Text>
              </div>
              <Button size="lg" onPress={upgradeToAnnualPlan} isDisabled={isUpdating}>
                Confirm
              </Button>
            </div>
          ) : (
            <ButtonLink size="lg" to={`mailto:${adminEmail}`} elementType="a">
              Contact an admin to upgrade today!
            </ButtonLink>
          )}
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}

export const SuccessModal = ({
  onCancel,
  nextInvoiceDate,
  isAlreadyOnAnnualPlan = false,
}: {
  onCancel: () => void
  nextInvoiceDate: string
  isAlreadyOnAnnualPlan?: boolean
}) => {
  let title = 'Smart choice. '
  if (isAlreadyOnAnnualPlan) {
    title += 'You are already on annual billing.'
  } else {
    title += 'Your savings have been applied.'
  }

  return (
    <Modal.Root onPressOutside={onCancel}>
      <div className={Styles.content}>
        <div className={cx(Styles.imgContainer, Styles.success)}>
          <h1 className={Styles.header}>{title}</h1>
        </div>
        <div className={Styles.successMessageContent}>
          <Heading level={4}>Your next invoice will be {nextInvoiceDate}</Heading>
          <div>
            <Text>We'd love to partner with you as you get set up.</Text>
            <Text>
              Register for our{' '}
              <a
                className={Styles.link}
                href="https://my.demio.com/ref/m3yjidEM28k8gbfw"
                target="_blank"
                rel="noreferrer"
              >
                webinar with live Q&A here
              </a>
              .
            </Text>
          </div>
        </div>
      </div>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button variant="tertiary" size="lg" onPress={onCancel}>
            Close
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Button size="lg" onPress={onCancel}>
            Got it!
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}

export const AnnualUpgrade = ({ handleClose = noop }: { handleClose?: () => void }) => {
  const { data, isLoading } = useCurrentPlan()
  const { openModal, closeAllModals } = useModals()
  const [hasModalOpened, setHasModalOpened] = useState(false)
  const [adminEmail, setAdminEmail] = useState('')
  const paymentPlans = usePaymentPlans()?.data?.payment_plans
  const currentPlan = paymentPlans?.find((plan) => plan.key === data?.plan)
  const userRole = useUserRole()
  const workspace = useCurrentWorkspace()
  const closeModalAndHandleClose = () => {
    handleClose()
    closeAllModals()
  }
  const { mutate: fetchAdminEmail, isLoading: isLoadingAdminEmail } = useMutation(
    async ({ workspaceKey }: { workspaceKey: string }) => WorkspaceClient.fetchAdminEmail(workspaceKey),
    {
      onSuccess: (res) => {
        setAdminEmail(res.data.admin_email)
      },
      onError: () => {
        showToastMessage('Unable to fetch admin email', 'error')
      },
    },
  )

  useEffect(() => {
    if (
      userRole &&
      ![UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(userRole) &&
      !adminEmail &&
      workspace &&
      !isLoadingAdminEmail
    ) {
      fetchAdminEmail({ workspaceKey: workspace.key })
    }
  }, [userRole, adminEmail, setAdminEmail, workspace, fetchAdminEmail, isLoadingAdminEmail])

  if (!hasModalOpened && !isLoading && data?.plan && userRole && workspace && currentPlan) {
    if (data?.billing_frequency === 'month') {
      openModal(AnnualUpgradeModal, {
        onCancel: closeModalAndHandleClose,
        workspace,
        currentPlan,
        isAdmin: userRole === UserRole.ADMIN,
        adminEmail,
      })
    } else {
      openModal(SuccessModal, {
        onCancel: closeModalAndHandleClose,
        nextInvoiceDate: dayjs(data?.next_payment?.due_date).format('MMMM D, YYYY'),
        isAlreadyOnAnnualPlan: true,
      })
    }

    setHasModalOpened(true)
  }

  return null
}
